/* Reviewed complementary offers. Shopify remains price/stock/cart authority. */
const configNode = document.getElementById("SeifePairingsConfig");
const cartLock = "seife-cart-mutation-v1";
const rawFetch = window.fetch.bind(window);
// Every Ajax cart writer in this theme shares the lock across tabs. Our own
// transaction holds it for its whole read/add/remove/reconcile sequence.
if (navigator.locks) window.fetch = function(input, init) {
  const url = new URL(typeof input === "string" || input instanceof URL ? input : input.url, location.href);
  const method = (init?.method || input?.method || "GET").toUpperCase();
  if (url.origin === location.origin && method === "POST" && /\/cart\/(add|change|update|clear)(\.js|\.json)?$/.test(url.pathname))
    return navigator.locks.request(cartLock, () => rawFetch(input, init));
  return rawFetch(input, init);
};
let earlyAdd;
const queueAdd = event => { earlyAdd = event.detail; };
document.addEventListener("seife:ordinary-added", queueAdd);
if (configNode) start().catch(error => console.warn("Product pairings unavailable.", error.message));

async function start() {
  const cfg = JSON.parse(configNode.textContent);
  const root = `${cfg.root.replace(/\/$/, "")}/`;
  const locale = cfg.locale.split("-")[0];
  const api = async (url, body) => {
    const response = await rawFetch(url, { method: body ? "POST" : "GET", cache: "no-store",
      headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw Error(`HTTP_${response.status}`);
    return response.json();
  };
  const [core, data, translations] = await Promise.all([import(cfg.core), api(cfg.catalog), api(cfg.copy)]);
  const t = translations[locale];
  if (!t) return; // Never substitute claim-bearing catalogue copy for a missing locale.
  const state = { cart: null, busy: false, offer: null };
  const pendingKey = "seife-pairing-pending-v1", dismissalKey = "seife-pairing-dismissed-v1";
  let renderUnsafe = false;
  let reviewedCart = null;
  const checkoutButtons = new Map();
  const hasPending = () => { try { return Boolean(localStorage.getItem(pendingKey)); } catch { return true; } };
  const guardCheckout = () => {
    const blocked = core.checkoutBlocked(hasPending(), renderUnsafe);
    document.querySelectorAll("[name=checkout],[data-cart-checkout]").forEach(button => {
      if (blocked) { if (!checkoutButtons.has(button)) checkoutButtons.set(button, button.disabled); button.disabled = true; }
      else if (checkoutButtons.has(button)) { button.disabled = checkoutButtons.get(button); checkoutButtons.delete(button); }
    });
    if (blocked) {
      const notice = document.querySelector("[data-cart-status]");
      if (notice) notice.replaceChildren(document.createTextNode(`${t.uncertain} `),
        button(reviewedCart ? t.reviewConfirm : t.review, async () => {
          const review = async () => {
            try {
              const latest = await readCart();
              if (reviewedCart && reviewedCart === core.fingerprint(latest)) {
                localStorage.removeItem(pendingKey);
                renderUnsafe = false; reviewedCart = null;
                guardCheckout(); notice.textContent = "";
              } else {
                await syncCart(latest); // Actual lines, plans and totals must render first.
                if (renderUnsafe) return;
                reviewedCart = core.fingerprint(latest);
                guardCheckout();
              }
            } catch { notice.textContent = t.uncertain; }
          };
          if (navigator.locks) await navigator.locks.request(cartLock, review);
          else await review();
        }));
    }
  };
  window.addEventListener("storage", event => {
    if (event.key === pendingKey) { renderUnsafe = true; reviewedCart = null; guardCheckout(); }
  });
  document.addEventListener("submit", event => {
    if (core.checkoutBlocked(hasPending(), renderUnsafe) && (event.submitter?.name === "checkout" || /\/checkout/.test(event.target.action || ""))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener("click", event => {
    if (core.checkoutBlocked(hasPending(), renderUnsafe) && event.target.closest('a[href*="/checkout"],[name=checkout],[data-cart-checkout]')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  const readCart = () => api(`${root}cart.js`);
  const announceCart = cart => {
    document.querySelectorAll("[data-cart-count]").forEach(node => { node.textContent = cart.item_count; node.hidden = !cart.item_count; });
    document.querySelectorAll("[data-cart-control]").forEach(control => {
      const label = `${control.dataset.cartLabel || t.cart} — ${cart.item_count}`;
      control.setAttribute("aria-label", label);
      const live = control.querySelector("[data-cart-live]");
      if (live) live.textContent = label;
    });
  };
  const format = cents => new Intl.NumberFormat(cfg.locale, { style: "currency", currency: state.cart?.currency || window.Shopify?.currency?.active || "CHF" }).format(cents / 100);
  const text = (key, args = {}) => Object.entries(args).reduce((s, [name, value]) => s.replaceAll(`{${name}}`, String(value)), t[key]);
  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (key === "text") node.textContent = value;
      else if (key.startsWith("on")) node.addEventListener(key.slice(2), value);
      else if (value !== false && value != null) node.setAttribute(key, value === true ? "" : value);
    }
    node.append(...children.filter(Boolean)); return node;
  };
  const button = (label, action, attrs = {}) => el("button", { type: "button", class: "button", text: label, onclick: action, ...attrs });
  const event = (action, surface) => document.dispatchEvent(new CustomEvent("seife:offer-event", { detail: { action, surface } }));
  const seen = new Set();
  const observe = (node, surface) => {
    if (seen.has(surface)) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { seen.add(surface); event("impression", surface); observer.disconnect(); }
    }, { threshold: 0.2 });
    observer.observe(node);
  };
  const setStatus = (node, message, error = false) => {
    node.textContent = message; node.classList.toggle("error", error);
  };
  const statusNode = () => el("p", { class: "seife-pairings__status", role: "status", "aria-live": "polite" });
  const product = handle => api(`${root}products/${encodeURIComponent(handle)}.js`);
  const offer = async () => {
    const result = await api(cfg.offer);
    state.offer = result?.ready && result.currency === "CHF" && Array.isArray(result.variants) ? result : null;
    return state.offer;
  };
  const planVariant = item => navigator.locks && state.cart?.currency === "CHF" && !core.protectedLine(item)
    ? state.offer?.variants.find(variant => variant.id === String(item.variant_id) && variant.available) : null;
  const dirtyCart = () => {
    const form = document.querySelector("[data-cart-form]");
    return form?.dataset.submitting === "true" || [...document.querySelectorAll("[data-cart-quantity]")]
      .some(input => input.value !== input.defaultValue);
  };
  async function syncCart(cart) {
    state.cart = cart;
    announceCart(cart);
    document.dispatchEvent(new CustomEvent("gift-shopping:cart-updated", { detail: { cart } }));
    const cartForm = document.querySelector("[data-cart-form]");
    if (cartForm) {
      // Section rendering updates purchased lines/totals while retaining this form's
      // note/discount inputs and existing delegated quantity/submit handlers.
      // Fetch the current theme's rendered cart. Section IDs can differ between
      // the published theme and an acceptance preview; never request a section
      // ID against the wrong theme and interpret its null HTML as cart failure.
      const viewUrl = new URL(`${root}cart`, location.origin);
      if (window.Shopify?.theme?.role && window.Shopify.theme.role !== "main")
        viewUrl.searchParams.set("preview_theme_id", String(window.Shopify.theme.id));
      const response = await fetch(viewUrl, { cache: "no-store", headers: { Accept: "text/html" }, signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw Error("CART_RENDER");
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      for (const selector of [".cart-lines", ".cart-total"]) {
        const next = parsed.querySelector(selector), current = cartForm.querySelector(selector);
        if (!next || !current) throw Error("CART_RENDER");
        current.replaceChildren(...next.childNodes);
      }
      cartForm.querySelector("[name=checkout]").disabled = Boolean(parsed.querySelector("[name=checkout]")?.disabled);
      checkoutButtons.delete(cartForm.querySelector("[name=checkout]"));
    }
    await renderSurfaces();
    renderUnsafe = false;
    guardCheckout();
  }
  function rememberPending(value) {
    localStorage.setItem(pendingKey, JSON.stringify(value));
    if (localStorage.getItem(pendingKey) !== JSON.stringify(value)) throw Error("STORAGE");
    guardCheckout();
  }
  async function settlePending(pending, cart) {
    const confirmed = pending.kind === "add" ? core.exactAdded(cart, pending.marker, pending.variantId)
      : core.conversionMatches(pending.before, cart, pending.key, pending.body);
    if (!confirmed) throw Error("UNCERTAIN");
    localStorage.removeItem(pendingKey);
    reviewedCart = null;
    try { await syncCart(cart); }
    catch (error) {
      // The mutation is confirmed. A view-refresh failure must never tell the
      // customer to repeat a successful add/conversion.
      state.cart = cart;
      renderUnsafe = true;
      guardCheckout();
      const form = document.querySelector("[data-cart-form]");
      if (form) {
        form.querySelector("[name=checkout]").disabled = true;
        const notice = form.querySelector("[data-cart-status]");
        notice.replaceChildren(document.createTextNode(`${pending.kind === "add" ? t.addedExtra : t.converted} `),
          el("a", { href: `${root}cart`, text: t.cart }));
      }
      console.warn("Cart confirmed; refresh needed.", error.message);
    }
  }
  async function mutation(status, operation, locked = false) {
    if (!locked && navigator.locks) return navigator.locks.request(cartLock, () => mutation(status, operation, true));
    if (state.busy) return;
    if (dirtyCart()) { setStatus(status, t.saveCart, true); return; }
    state.busy = true;
    const form = document.querySelector("[data-cart-form]");
    if (form) { form.inert = true; form.dataset.pairingBusy = "true"; }
    setStatus(status, t.busy);
    try {
      const previous = localStorage.getItem(pendingKey);
      if (previous) { await settlePending(JSON.parse(previous), await readCart()); setStatus(status, t.cart); return; }
      await operation();
    } catch (error) {
      let pending;
      try { pending = JSON.parse(localStorage.getItem(pendingKey) || "null"); } catch { /* storage denied */ }
      if (pending) {
        try { await settlePending(pending, await readCart()); setStatus(status, pending.kind === "add" ? t.addedExtra : t.converted); return; }
        catch { setStatus(status, t.uncertain, true); }
      } else setStatus(status, error.message === "CART_CHANGED" ? t.cartChanged
        : error.message === "UNAVAILABLE" ? t.unavailable : t.failure, true);
      if (error.message === "CART_RENDER" && form) form.querySelector("[name=checkout]").disabled = true;
    } finally {
      state.busy = false;
      if (form) { form.inert = false; delete form.dataset.pairingBusy; }
      if (hasPending()) {
        guardCheckout();
        status.append(document.createTextNode(" "), el("a", { href: `${root}cart`, text: t.cart }));
      }
      if (status.textContent && !status.isConnected) {
        document.querySelectorAll("[data-seife-pairings] .seife-pairings__status").forEach(node => setStatus(node, status.textContent, status.classList.contains("error")));
      }
    }
  }
  async function addTarget(target, displayedPrice, status, addButton, surface) {
    await mutation(status, async () => {
      addButton.disabled = true;
      const [latest, cart] = await Promise.all([product(target.handle), readCart()]);
      const variant = latest.variants?.find(v => String(v.id) === target.variantId);
      if (!variant?.available || variant.price !== displayedPrice || String(latest.id) !== target.productId
        || cart.items.some(item => String(item.product_id) === target.productId)) throw Error("UNAVAILABLE");
      const marker = crypto.randomUUID();
      rememberPending({ kind: "add", marker, variantId: target.variantId });
      // Unique marker allows reconciliation without matching unrelated additions.
      try {
        await api(`${root}cart/add.js`, { items: [{ id: target.variantId, quantity: 1, properties: { _seife_pairing_request: marker } }] });
      } catch (error) {
        if (/^HTTP_(400|404|422)$/.test(error.message)) {
          const after = await readCart();
          if (!after.items.some(item => item.properties?._seife_pairing_request === marker)) localStorage.removeItem(pendingKey);
        }
        throw error;
      }
      await settlePending({ kind: "add", marker, variantId: target.variantId }, await readCart());
      setStatus(status, t.addedExtra); event("add", surface);
      addButton.textContent = t.addedExtra;
    });
    try {
      if (!localStorage.getItem(pendingKey) && addButton.textContent !== t.addedExtra) addButton.disabled = false;
    } catch { addButton.disabled = false; }
  }
  async function cards(container, items, status, surface, limit) {
    const targets = core.recommendations(data, items, state.cart, 20);
    let count = 0;
    for (const target of targets) {
      if (count >= limit) break;
      try {
        const live = await product(target.handle);
        const variant = live.variants?.find(v => String(v.id) === target.variantId && v.available);
        if (String(live.id) !== target.productId || !variant || live.requires_selling_plan || !Number.isSafeInteger(variant.price)
          || !target.title[locale] || !target.reason[locale]) continue;
        const url = `${root}products/${target.handle}?variant=${target.variantId}`;
        const image = variant.featured_image?.src || live.featured_image;
        if (!image || !/^https:\/\//.test(new URL(image, location.origin).href)) continue;
        const add = button(t.add, () => addTarget(target, variant.price, status, add, surface));
        container.append(el("article", { class: "seife-pairing" }, [
          el("a", { href: url, "aria-label": target.title[locale] }, [el("img", { src: image, alt: target.title[locale], loading: "lazy", width: "400", height: "400" })]),
          el("h3", {}, [el("a", { href: url, text: target.title[locale] })]),
          el("p", { text: target.reason[locale] }),
          el("p", { class: "seife-pairing__price", text: format(variant.price) }),
          add,
        ]));
        count++;
      } catch { /* Unavailable/unsupported product is omitted, never invented. */ }
    }
    return count;
  }
  async function configureSubscription(item, host, status, surface) {
    event("subscription_open", surface);
    const snapshot = await readCart();
    const source = snapshot.items.find(line => line.key === item.key);
    if (!source || core.protectedLine(source) || !core.sourceFor(data, source)) throw Error("CART_CHANGED");
    await offer();
    const eligible = planVariant(source);
    if (!eligible?.plans.length) throw Error("UNAVAILABLE");
    const live = await product(core.sourceFor(data, source).handle);
    const liveVariant = live.variants?.find(v => String(v.id) === String(source.variant_id) && v.available);
    const plans = eligible.plans.filter(plan => [4, 6, 8, 12].includes(plan.intervalWeeks)
      && liveVariant?.selling_plan_allocations?.some(allocation => String(allocation.selling_plan_id) === plan.id));
    if (!plans.length) throw Error("UNAVAILABLE");
    const select = el("select", { "aria-label": t.interval }, plans.map(plan => el("option", { value: plan.id, text: text("weeks", { weeks: plan.intervalWeeks }) })));
    const amount = el("p", { class: "seife-upsell__terms" });
    const price = () => liveVariant.selling_plan_allocations.find(a => String(a.selling_plan_id) === select.value)?.price;
    const showPrice = () => { amount.textContent = text("recurring", { price: format(price() * source.quantity), quantity: source.quantity }); };
    select.addEventListener("change", showPrice); showPrice();
    const consent = el("input", { type: "checkbox" });
    const confirm = button(t.confirm, () => mutation(status, async () => {
      if (!consent.checked) return;
      confirm.disabled = true;
      const plan = plans.find(p => p.id === select.value), requestId = crypto.randomUUID();
      const checked = await api(cfg.offer, { requestId, intervalWeeks: plan.intervalWeeks,
        lines: [{ variantId: String(source.variant_id), sellingPlanId: plan.id, quantity: source.quantity }] });
      const verified = checked.items?.[0];
      if (checked.requestId !== requestId || checked.currency !== "CHF" || checked.items?.length !== 1
        || String(verified?.id) !== String(source.variant_id) || String(verified?.selling_plan) !== plan.id
        || verified.quantity !== source.quantity || verified.unitPriceCents !== price()
        || checked.totalCents !== price() * source.quantity
        || verified.properties?._seifenabo_request_id !== requestId) throw Error("UNAVAILABLE");
      const before = await readCart();
      if (core.fingerprint(before) !== core.fingerprint(snapshot)) throw Error("CART_CHANGED");
      const body = core.conversionBody(before, source.key, plan.id, verified.properties);
      const pending = { kind: "convert", before: { items: before.items.map(line => ({
        key: line.key, variant_id: line.variant_id, quantity: line.quantity,
        properties: line.properties, selling_plan_allocation: line.selling_plan_allocation,
      })) }, key: source.key, body };
      rememberPending(pending);
      const confirmedCart = await core.replaceExactLine(before, source.key, body, requestId,
        (operation, payload) => api(`${root}cart/${operation}.js`, payload), readCart);
      // This response IS the authoritative resulting cart. Do not discard it and
      // turn a later failed GET into an uncertain successful conversion.
      await settlePending(pending, confirmedCart);
      setStatus(status, t.converted); event("subscription_convert", surface);
      host.replaceChildren(el("p", { text: t.converted }), el("a", { class: "button", href: `${root}cart`, text: t.cart }));
    }).finally(() => { confirm.disabled = !consent.checked; }), { disabled: true });
    consent.addEventListener("change", () => { confirm.disabled = !consent.checked; });
    host.replaceChildren(el("h3", { text: t.upsell }), el("p", { text: text("replace", { quantity: source.quantity }) }),
      el("label", {}, [document.createTextNode(t.interval), select]), amount,
      el("p", { class: "seife-upsell__terms", text: t.shipping }),
      el("label", {}, [consent, document.createTextNode(` ${t.consent}`)]), confirm,
      button(t.cancel, () => host.replaceWith(upsell(source, status, surface)), { class: "button button--secondary" }));
  }
  function upsell(item, status, surface, pdp = false) {
    if (!planVariant(item)) return null;
    const host = el("section", { class: "seife-upsell" });
    host.append(el("h3", { text: t.upsell }), el("p", { text: t.upsellBody }),
      button(t.upsellButton, async () => {
        try {
          // Before adding, reuse the existing verified PDP wizard. After adding,
          // configure a specific current line through the locked keyed replacement.
          const cart = await readCart(); state.cart = cart;
          const matching = cart.items.filter(line => String(line.variant_id) === String(item.variant_id) && !core.protectedLine(line));
          const line = item.key ? cart.items.find(line => line.key === item.key) : matching.length === 1 ? matching[0] : null;
          if (line) await configureSubscription(line, host, status, surface);
          else if (pdp && !matching.length) {
            const opener = document.querySelector("[data-product-abo-open]");
            if (!opener || opener.hidden) throw Error("UNAVAILABLE");
            event("subscription_open", surface); opener.click();
          } else { setStatus(status, t.cartChanged, true); }
        } catch (error) { setStatus(status, error.message === "CART_CHANGED" ? t.cartChanged : t.unavailable, true); }
      }));
    return host;
  }
  let renderVersion = 0;
  async function renderSurfaces() {
    if (!state.cart) return;
    const version = ++renderVersion;
    for (const section of document.querySelectorAll("[data-seife-pairings]")) {
      const surface = section.dataset.seifePairings;
      const source = data.sources.find(item => item.id === cfg.productId);
      const selected = document.querySelector("[data-variant-id]")?.value;
      const items = surface === "cart" ? state.cart.items : source && selected
        ? [{ product_id: source.id, variant_id: selected, quantity: 1, properties: {} }] : [];
      const status = statusNode(), grid = el("div", { class: "seife-pairings__grid" });
      if (surface === "cart") {
        grid.setAttribute("tabindex", "0");
        grid.setAttribute("role", "region");
        grid.setAttribute("aria-label", t.cartHeading);
      }
      const count = await cards(grid, items, status, surface, surface === "cart" ? 2 : 3);
      if (version !== renderVersion) return;
      const eligible = items.find(item => core.sourceFor(data, item) && planVariant(item));
      const invitation = eligible && upsell(eligible, status, surface, surface === "product");
      section.replaceChildren(...(count ? [el("h2", { text: surface === "cart" ? t.cartHeading : t.heading }), grid] : []),
        ...(invitation ? [invitation] : []), status);
      section.hidden = !count && !invitation;
      if (surface === "product" && count) document.querySelector(".product-related")?.setAttribute("hidden", "");
      if (count || invitation) observe(section, surface);
    }
  }
  async function showAdded(detail) {
    try {
      if (Array.isArray(detail.cart?.items)) announceCart(detail.cart);
      if (sessionStorage.getItem(dismissalKey) || document.querySelector("dialog[open]")
        || state.busy || !detail.key || !Array.isArray(detail.cart?.items)) return;
      state.cart = detail.cart;
      const item = state.cart.items.find(line => line.key === detail.key);
      if (!item || core.protectedLine(item) || !core.sourceFor(data, item)) return;
      await offer().catch(() => null);
      const status = statusNode(), grid = el("div", { class: "seife-pairings__grid" });
      const count = await cards(grid, [item], status, "post-add", 2);
      const invitation = upsell(item, status, "post-add");
      if (!count && !invitation || document.querySelector("dialog[open]")) return;
      const trigger = document.activeElement;
      const dialog = el("dialog", { class: "seife-pairings-dialog seife-pairings seife-pairings--compact", "aria-labelledby": "SeifePairingsTitle" });
      const close = () => dialog.close();
      dialog.append(button("×", close, { class: "seife-pairings__close", "aria-label": t.close }),
        el("h2", { id: "SeifePairingsTitle", text: t.added }),
        ...(invitation ? [invitation] : []), grid, status,
        el("div", { class: "seife-pairings__actions" }, [
          button(t.continue, close, { class: "button button--secondary" }),
          el("a", { class: "button", href: `${root}cart`, text: t.cart }),
        ]));
      dialog.addEventListener("close", () => {
        sessionStorage.setItem(dismissalKey, "1"); event("dismiss", "post-add");
        dialog.remove(); if (trigger?.isConnected) trigger.focus();
      });
      dialog.addEventListener("click", e => {
        const rect = dialog.getBoundingClientRect();
        if (e.target === dialog && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) close();
      });
      document.body.append(dialog); dialog.showModal(); dialog.querySelector("button").focus();
      event("impression", "post-add");
    } catch { /* Offers never turn a confirmed add into a failure. */ }
  }
  document.removeEventListener("seife:ordinary-added", queueAdd);
  document.addEventListener("seife:ordinary-added", event => { showAdded(event.detail); });
  document.addEventListener("change", event => {
    if (event.target.closest("[data-product-purchase]")) setTimeout(() => renderSurfaces(), 0);
  });
  document.addEventListener("click", event => {
    if (event.target.closest("[data-option-value]")) setTimeout(() => renderSurfaces(), 0);
  });
  if (document.querySelector("[data-seife-pairings]")) {
    state.cart = await readCart();
    const saved = localStorage.getItem(pendingKey);
    if (saved) {
      try { await settlePending(JSON.parse(saved), state.cart); } catch { guardCheckout(); }
    }
    await offer().catch(() => null);
    await renderSurfaces();
    guardCheckout();
    // No automatic mutation on reload. Existing uncertain requests are read-only
    // reconciled by the next explicit action or the customer can inspect the cart.
  }
  if (earlyAdd) { const detail = earlyAdd; earlyAdd = null; await showAdded(detail); }
}
